import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { orderingDir, orderingField, toggleOrdering, type Ordering, type SortDir } from "@/shared/lib/ordering";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/ui/Tooltip";

interface SortHeaderProps<F extends string> {
  label: ReactNode;
  field: F;
  /** The table's current ordering ("field" / "-field"), or null when unsorted. */
  ordering: string | null;
  onSortChange: (next: Ordering<F>) => void;
  /** Direction of the first click on this column (default "desc": biggest / latest first). */
  firstDir?: SortDir;
  align?: "left" | "right";
  /** Optional explanation shown in a tooltip next to the label. */
  hint?: string;
  /** Classes for the <th> itself, so each table keeps its own header look. */
  className?: string;
}

// Clickable column header: first click sorts by the column, the next click flips asc/desc.
export function SortHeader<F extends string>({
  label,
  field,
  ordering,
  onSortChange,
  firstDir = "desc",
  align = "left",
  hint,
  className,
}: SortHeaderProps<F>) {
  const { t } = useTranslation();
  const active = orderingField(ordering) === field;
  const dir = orderingDir(ordering);
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ArrowUp : ArrowDown;

  return (
    <th
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn("whitespace-nowrap", align === "right" ? "text-right" : "text-left", className)}
    >
      <span className={cn("inline-flex items-center gap-1", align === "right" && "flex-row-reverse")}>
        <button
          type="button"
          onClick={() => onSortChange(toggleOrdering(ordering, field, firstDir))}
          title={t("table.sortBy")}
          className={cn(
            "group/sort -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors",
            "hover:text-grey-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green/40",
            align === "right" && "flex-row-reverse",
            active && "text-grey-900",
          )}
        >
          {label}
          <Icon
            size={13}
            strokeWidth={2}
            className={cn(
              "flex-none transition-colors",
              active ? "text-green-dark" : "text-grey-300 group-hover/sort:text-grey-500",
            )}
            aria-hidden
          />
        </button>
        {hint && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  aria-label={hint}
                  className="inline-flex cursor-help text-grey-300 hover:text-grey-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green/40"
                >
                  <Info size={13} strokeWidth={2} aria-hidden />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[260px] whitespace-normal font-normal">{hint}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
    </th>
  );
}
